import { Component, computed, effect, ElementRef, inject, signal, viewChild, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { InputComponent } from '../../components/input/input.component';
import { ButtonComponent } from '../../components/button/button.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import TYPES from '../../constants/types';
import { HeaderComponent } from '../../components/header/header.component';
import { TextAreaComponent } from '../../components/text-area/text-area.component';
import { UserPsychologistService } from '../../services/user-psychologist.service';
import { UserPsychologist } from '../../models/user-psychologist';
import { PsychologistService } from '../../services/psychologist.service';
import { Psychologist } from '../../models/psychologists';
import { User } from '../../models/user';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { DATABASE_SERVICE, DatabaseService } from '../../services/database.service';
import { Appointment } from '../../models/appointment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EdgeFunctionCallerService } from '../../services/edge-function-caller.service';
import { MatCheckbox } from '@angular/material/checkbox';
import { AUTH_SERVICE, AuthService } from '../../services/auth.service';
import { LoadingComponent } from '../../components/loading/loading.component';
import { addLocalWeeksToIso, combineLocalDateAndTimeToIso } from '../../util/appointment-date.util';

@Component({
  selector: 'app-add-appointment',
  imports: [
    InputComponent,
    TextAreaComponent,
    ButtonComponent,
    TranslatePipe,
    ReactiveFormsModule,
    HeaderComponent,
    LoadingComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckbox
  ],
  templateUrl: './add-appointment.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './add-appointment.component.scss',
})
export class AddAppointmentComponent {
  authService: AuthService = inject(AUTH_SERVICE);
  psychologistService: PsychologistService<Psychologist> = inject(PsychologistService);
  userPsychologistService: UserPsychologistService<UserPsychologist> = inject(UserPsychologistService);
  appointmentService: DatabaseService<Appointment> = inject(DATABASE_SERVICE);
  edgeFunctionCallerService: EdgeFunctionCallerService = inject(EdgeFunctionCallerService);
  snackBar:MatSnackBar = inject(MatSnackBar);
  patients: WritableSignal<User[]> = signal<User[]>([]);
  appointment: WritableSignal<Appointment | null> = signal<Appointment | null>(null);
  loading: WritableSignal<boolean> = signal<boolean>(false);
  appoitmentForm: FormGroup;
  BUTTON_TYPES: any;
  INFO_SCREEN: any;
  CURRENCIES: any;
  DURATIONS: any;
  patientControl: UntypedFormControl;
  patientSearchTerm: WritableSignal<string> = signal('');
  patientSearchInput = viewChild<ElementRef<HTMLInputElement>>('patientSearchInput');
  filteredPatients = computed(() => {
    const term = this.patientSearchTerm().trim().toLowerCase();

    if(!term){
      return this.patients();
    }

    return this.patients().filter((patient) => {
      const fullName = `${patient.first_name ?? ''} ${patient.last_name ?? ''}`.toLowerCase();
      return fullName.includes(term);
    });
  });
  titleControl: UntypedFormControl;
  descriptionControl: UntypedFormControl;
  dateControl: UntypedFormControl;
  timeControl: UntypedFormControl;  
  durationControl: UntypedFormControl;  
  valueControl: UntypedFormControl;  
  currencyControl: UntypedFormControl;  
  calendarControl: UntypedFormControl;
  confirmationControl: UntypedFormControl;
  recurringWeeklyControl: UntypedFormControl;
  recurrenceOccurrencesControl: UntypedFormControl;

  constructor(
    private formBuilder: FormBuilder,
    private translate: TranslateService,
    private router: Router
  ) {
    
    this.loadData();
    
    this.BUTTON_TYPES = TYPES.UI.BUTTON;
    this.INFO_SCREEN = TYPES.UI.INFO_SCREEN;
    this.CURRENCIES = TYPES.DATABASE.CURRENCY;
    this.DURATIONS = TYPES.DATABASE.DURATION;

    let initialTime = new Date();
    initialTime.setHours(8, 0, 0, 0); //Start timepicker at 8 o'clock 

    this.patientControl = new UntypedFormControl('', [Validators.required]);
    this.titleControl = new UntypedFormControl('', [Validators.required]);
    this.descriptionControl = new UntypedFormControl('',[]);
    this.dateControl = new UntypedFormControl('', [Validators.required]);
    this.timeControl = new UntypedFormControl(initialTime, [Validators.required]);
    this.durationControl = new UntypedFormControl('', [Validators.required]);
    this.valueControl = new UntypedFormControl('', [Validators.required]);
    this.currencyControl = new UntypedFormControl('', [Validators.required]);
    this.calendarControl = new UntypedFormControl(false, []);
    this.confirmationControl = new UntypedFormControl(false, []);
    this.recurringWeeklyControl = new UntypedFormControl(false, []);
    this.recurrenceOccurrencesControl = new UntypedFormControl(2, [Validators.required, Validators.min(2)]);
      
    this.appoitmentForm = this.formBuilder.group({
      patient: this.patientControl,
      title: this.titleControl,
      description: this.descriptionControl,
      date: this.dateControl,
      time: this.timeControl,
      duration: this.durationControl,
      value: this.valueControl,
      currency: this.currencyControl,
      calendar: this.calendarControl,
      confirmation: this.confirmationControl
    });

    this.recurringWeeklyControl.valueChanges.subscribe((isRecurringWeekly: boolean) => {
      if(!isRecurringWeekly){
        this.recurrenceOccurrencesControl.setValue(2);
        this.recurrenceOccurrencesControl.markAsPristine();
        this.recurrenceOccurrencesControl.markAsUntouched();
      }
    });

    const navigation = this.router.currentNavigation();
    this.appointment.set(navigation?.extras.state?.['appointment'] ?? null);

    effect(() => {
      if(this.appointment()){
        this.patientControl.patchValue(this.appointment()?.user_id ?? '');
        this.titleControl.patchValue(this.appointment()?.title ?? ''); 
        this.descriptionControl.patchValue(this.appointment()?.description ?? '');
        this.dateControl.patchValue(this.appointment()?.date ? new Date(this.appointment()!.date!) : '');
        this.timeControl.patchValue(this.appointment()?.date ? new Date(this.appointment()!.date!) : '');
        this.durationControl.patchValue(this.appointment()?.duration ?? '');
        this.valueControl.patchValue(this.appointment()?.value ?? '');
        this.currencyControl.patchValue(this.appointment()?.currency ?? '');
        this.calendarControl.patchValue(this.appointment()?.calendar_id ? true : false);
      }
    });
  }

  async loadData(){
    
    if(!this.authService.user()){
      await this.authService.getUser();
    }

    let psychologistId = this.psychologistService.psychologist()?.id;

    if(!psychologistId){
      const userId = this.authService.user()?.id;
      if(userId){
        await this.psychologistService.getPsychologistByUserId(userId);
        psychologistId = this.psychologistService.psychologist()?.id;
      }
    }

    if(psychologistId){
      this.userPsychologistService.getAllActiveUsersPsychologistsByPsychologistId(psychologistId).then(
        (data) => {
          if(data){
            for(let userPsychologist of data){
              let activeUser = userPsychologist.users;
              if(activeUser){
                this.patients.update(
                  (current) => {
                    return [...current, activeUser];
                  }
                )
              }
            }
          }
        }
      );      
    }
  }

  async createAppoitment() {
    if(this.loading()){
      return;
    }

    if(this.isFormInvalid()){
      this.appoitmentForm.markAllAsTouched();
      this.appoitmentForm.updateValueAndValidity();
      if(this.recurringWeeklyControl.value){
        this.recurrenceOccurrencesControl.markAsTouched();
        this.recurrenceOccurrencesControl.updateValueAndValidity();
      }
      return;
    }

    this.loading.set(true);

    try{
      const appointmentId = this.appointment()?.id;
      const isRecurringWeekly = !appointmentId && !!this.recurringWeeklyControl.value;
      const occurrences = isRecurringWeekly ? Number(this.recurrenceOccurrencesControl.value) : 1;
      
      const baseAppointment: Partial<Appointment> = {
        title: this.appoitmentForm.getRawValue().title,
        description: this.appoitmentForm.getRawValue().description,
        date: combineLocalDateAndTimeToIso(this.appoitmentForm.getRawValue().date, this.appoitmentForm.getRawValue().time),
        duration: this.appoitmentForm.getRawValue().duration,
        user_id: this.appoitmentForm.getRawValue().patient,
        psychologist_id: this.psychologistService.psychologist()?.id,
        status: this.confirmationControl.value ? TYPES.DATABASE.STATUS.WAITING_CONFIRMATION.code : TYPES.DATABASE.STATUS.CONFIRMED.code,
        value: this.appoitmentForm.getRawValue().value,
        currency: this.appoitmentForm.getRawValue().currency,
        calendar_id: this.appointment()?.calendar_id
      };

      let message:string = '';
      if(appointmentId){
        const appointment = {...baseAppointment};

        if(this.appoitmentForm.getRawValue().calendar){
          const action = appointment.calendar_id ? "update" : "create";
          const calendarEvent = await this.syncAppointmentToGoogleCalendar(appointment, action);

          if(calendarEvent){
            appointment.calendar_id = calendarEvent.eventId;
            appointment.meet_link = calendarEvent.meetLink;
          }                  
        }

        await this.appointmentService.update(
          TYPES.DATABASE.TABLES.APPOINTMENTS,
          appointmentId,
          appointment  
        );

        message = this.translate.instant('info.appointment_updated');
      }else{
        const appointmentsToCreate = this.buildWeeklyAppointments(baseAppointment, occurrences);

        for(const appointmentToCreate of appointmentsToCreate){
          if(this.appoitmentForm.getRawValue().calendar){
            const calendarEvent = await this.syncAppointmentToGoogleCalendar(appointmentToCreate, "create");

            if(calendarEvent){
              appointmentToCreate.calendar_id = calendarEvent.eventId;
              appointmentToCreate.meet_link = calendarEvent.meetLink;
            }
          }

          await this.appointmentService.create(
            TYPES.DATABASE.TABLES.APPOINTMENTS,
            appointmentToCreate
          );
        }

        message = occurrences > 1
          ? this.translate.instant('info.appointments_added', {count: occurrences})
          : this.translate.instant('info.appointment_added');
      }

      if(baseAppointment.user_id){
        await this.edgeFunctionCallerService.addPushNotification(
          baseAppointment.user_id,
          new Date().toISOString(),
          this.translate.instant(TYPES.DATABASE.NOTIFICATION.APPOINTMENT_CREATED.title),
          this.translate.instant(TYPES.DATABASE.NOTIFICATION.APPOINTMENT_CREATED.body),
          {
            onActionClick: {
              default: {operation: "navigateLastFocusedOrOpen", url: "/home"}
            }
          }
        );
      }
      
      const action:string = this.translate.instant('buttons.dismiss');
      this.snackBar.open(message, action);            

      this.router.navigate(['/calendar'], { replaceUrl: true });
    }catch(error: any){
      const message = this.translate.instant('info.unkonwn_error', { error: error?.toString() ?? String(error) });
      const action:string = this.translate.instant('buttons.dismiss');
      this.snackBar.open(message, action);
    }finally{
      this.loading.set(false);
    }
  }

  back()  {
    history.back();
  }

  isFormInvalid(): boolean{
    const recurrenceInvalid = !this.appointment()
      && this.recurringWeeklyControl.value
      && this.recurrenceOccurrencesControl.invalid;

    return this.appoitmentForm.invalid || recurrenceInvalid;
  }

  onPatientSelectOpened(opened: boolean): void {
    if(opened){
      setTimeout(() => this.patientSearchInput()?.nativeElement.focus());
      return;
    }

    this.patientSearchTerm.set('');
  }

  onPatientSearch(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.patientSearchTerm.set(target?.value ?? '');
  }

  buildWeeklyAppointments(baseAppointment: Partial<Appointment>, occurrences: number): Partial<Appointment>[] {
    const baseDate = baseAppointment.date ? new Date(baseAppointment.date) : null;
    const safeOccurrences = Math.max(1, occurrences);

    if(!baseDate){
      return [baseAppointment];
    }

    return Array.from({length: safeOccurrences}, (_, index) => {
      return {
        ...baseAppointment,
        date: addLocalWeeksToIso(baseDate.toISOString(), index),
        calendar_id: null,
        meet_link: null
      };
    });
  }

  async syncAppointmentToGoogleCalendar(appointment: Partial<Appointment>, action: "create" | "update" | "delete"){
    
    const userId = this.psychologistService.psychologist()?.user_id;
    const patient = this.patients().find((p) => p.id === appointment.user_id);
    
    if(userId
      && patient
      && patient.email
      && appointment.title
      && appointment.date
      && appointment.duration
    ){
      const calendarEvent = await this.edgeFunctionCallerService.googleCalendarEvent(
      {
          action: action,
          userId : userId,
          title: appointment.title,
          startDateTime: appointment.date,
          endDateTime: new Date(new Date(appointment.date).getTime() + appointment.duration * 60 * 1000).toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          eventId: appointment.calendar_id,
          patientEmail: patient.email
        }
      );
      return calendarEvent;
    }
  }


}
